﻿import { get as getProjection, transform } from 'ol/proj';
import { containsCoordinate } from 'ol/extent';

const MERCATOR_LIMIT = 20037508.34;
//CG: aria de utilizare EPSG:3844 (Romania), in lon/lat
const STEREO70_AREA = [20.26, 43.44, 31.41, 48.27];
const MAX_ROWS = 3;

//CG: detecteaza coordonate in textul cautarii (4326 / 3857 / 3844) si propune randuri "centreaza la punct"
export default class CoordinateSearch {
    constructor(mapControl, resourcesHandler) {
        this.mapControl = mapControl;
        this.resourcesHandler = resourcesHandler;
    }

    search(text) {
        let candidates = this._parse(text);
        if (candidates.length == 0) {
            return [];
        }

        return this._rank(candidates).slice(0, MAX_ROWS).map(x => ({
            IdMapSearch: null,
            Name: x.name,
            IconClass: 'fal fa-crosshairs',
            Wkt: null,
            Lon: x.lonLat[0],
            Lat: x.lonLat[1],
            DataSourceName: this.resourcesHandler.get('SearchCoordinate') + ' · ' + x.crsName,
            Type: 'COORDINATE'
        }));
    }

    //CG: WKT punct in proiectia hartii pentru un rand intors de search()
    toMapWkt(item) {
        let coord = transform([Number(item.Lon), Number(item.Lat)], 'EPSG:4326', this.mapControl.generalSettingsHandler.settings.ProjCode);
        return 'POINT(' + coord[0] + ' ' + coord[1] + ')';
    }

    _parse(text) {
        let value = (text || '').trim().toUpperCase()
            .replace(/[′’`]/g, "'").replace(/[″”]|''/g, '"').replace(/\s+/g, ' ');

        let labelled = this._labelledCandidates(value);
        if (labelled != null) {
            return labelled;
        }

        if (/^[-+\d\s.,;]+$/.test(value)) {
            let pair = this._splitPair(value);
            return pair != null ? this._numericCandidates(pair[0], pair[1]) : [];
        }

        if (/^[-+\d\s.,;°'"NSEW]+$/.test(value)) {
            return this._geographicCandidates(value);
        }

        return [];
    }

    //CG: formatul propriilor etichete ("x 1, y 2" / "N 2, E 1"), ca textul ales sa poata fi editat; axele sunt date, un singur candidat
    _labelledCandidates(value) {
        let match = value.match(/^([XYNE])\s*([-+]?\d+(?:[.,]\d+)?)\s*[,;]?\s*([XYNE])\s*([-+]?\d+(?:[.,]\d+)?)$/);
        let axes = match != null ? match[1] + match[3] : null;
        let viewProjection = this.mapControl.getViewProjection();

        //CG: x/y = Web Mercator, N/E = Stereo 70 (doar pe harta 3844)
        let isMercator = axes == 'XY' || axes == 'YX';
        let isStereo = (axes == 'NE' || axes == 'EN') && viewProjection == 'EPSG:3844';
        if (!isMercator && !isStereo) {
            return null;
        }

        let [first, second] = [this._toNumber(match[2]), this._toNumber(match[4])];
        let [e, n] = axes == 'XY' || axes == 'EN' ? [first, second] : [second, first];
        //CG: "N 45, E 25" cu valori mici ramane in grade (calea geografica)
        if (e == null || n == null || Math.max(Math.abs(e), Math.abs(n)) < 1000) {
            return value.startsWith('X') || value.startsWith('Y') ? [] : null;
        }

        let candidate = this._projectedCandidate(e, n, viewProjection);
        return candidate != null ? [candidate] : [];
    }

    //CG: "45 25", "45,25", "45, 25", "45;25", "45,5 25,3" (virgula zecimala), "45.5,25.3"
    _splitPair(value) {
        let parts;
        if (value.includes(';')) {
            parts = value.split(';');
        } else if (/,\s/.test(value)) {
            parts = value.split(/,\s+/);
        } else if (value.includes(' ')) {
            parts = value.split(' ');
        } else {
            parts = value.split(',');
        }

        if (parts.length != 2) {
            return null;
        }

        let numbers = parts.map(x => this._toNumber(x.trim()));
        return numbers.every(x => x != null) ? numbers : null;
    }

    _toNumber(token) {
        if (!/^[-+]?\d+([.,]\d+)?$/.test(token)) {
            return null;
        }
        let number = Number(token.replace(',', '.'));
        return Number.isFinite(number) ? number : null;
    }

    _numericCandidates(a, b) {
        let candidates = [];

        //CG: grade - ambele ordini (lat lon / lon lat), clasamentul decide
        if (Math.abs(a) <= 90 && Math.abs(b) <= 180) {
            candidates.push(this._wgs84Candidate(b, a));
        }
        if (Math.abs(b) <= 90 && Math.abs(a) <= 180 && a != b) {
            candidates.push(this._wgs84Candidate(a, b));
        }

        //CG: metri doar in proiectia hartii (3857 sau 3844); valori mici = grade, nu metri
        let viewProjection = this.mapControl.getViewProjection();
        if ((viewProjection != 'EPSG:3857' && viewProjection != 'EPSG:3844') || Math.max(Math.abs(a), Math.abs(b)) < 1000) {
            return candidates;
        }

        [[a, b], [b, a]].forEach(([e, n], i) => {
            if (i == 1 && a == b) {
                return;
            }

            let candidate = this._projectedCandidate(e, n, viewProjection);
            if (candidate != null) {
                candidates.push(candidate);
            }
        });

        return candidates;
    }

    _projectedCandidate(e, n, projection) {
        if (projection == 'EPSG:3857' && Math.abs(e) <= MERCATOR_LIMIT && Math.abs(n) <= MERCATOR_LIMIT) {
            return {
                lonLat: transform([e, n], 'EPSG:3857', 'EPSG:4326'),
                name: 'x ' + this._format(e, 2) + ', y ' + this._format(n, 2),
                crsName: 'Web Mercator'
            };
        }

        if (projection == 'EPSG:3844' && e > 0 && n > 0) {
            let lonLat = transform([e, n], 'EPSG:3844', 'EPSG:4326');
            if (containsCoordinate(STEREO70_AREA, lonLat)) {
                return {
                    lonLat: lonLat,
                    name: 'N ' + this._format(n, 2) + ', E ' + this._format(e, 2),
                    crsName: 'Stereo 70'
                };
            }
        }

        return null;
    }

    //CG: grade cu °, ', ", si/sau emisfere: 45°30'15"N 25°10'E, N45.5 E25.3, 45.5N, 25.3E
    _geographicCandidates(value) {
        let leadingHemisphere = /^[NSEW]/.test(value);
        let pattern = leadingHemisphere
            ? /([NSEW])\s*([-+]?\d+(?:[.,]\d+)?)(?:\s*°)?(?:\s*(\d+(?:[.,]\d+)?)\s*')?(?:\s*(\d+(?:[.,]\d+)?)\s*")?/g
            : /([-+]?\d+(?:[.,]\d+)?)(?:\s*°)?(?:\s*(\d+(?:[.,]\d+)?)\s*')?(?:\s*(\d+(?:[.,]\d+)?)\s*")?\s*([NSEW])?/g;

        let matches = [...value.matchAll(pattern)];
        if (matches.length != 2 || value.replace(pattern, '').replace(/[\s,;]/g, '') != '') {
            return [];
        }

        let parts = matches.map(m => {
            let [hemisphere, deg, min, sec] = leadingHemisphere ? [m[1], m[2], m[3], m[4]] : [m[4], m[1], m[2], m[3]];
            let degrees = Math.abs(Number(deg.replace(',', '.')));
            degrees += Number((min || '0').replace(',', '.')) / 60 + Number((sec || '0').replace(',', '.')) / 3600;
            let negative = deg.startsWith('-') || hemisphere == 'S' || hemisphere == 'W';
            return { value: negative ? -degrees : degrees, hemisphere: hemisphere };
        });

        let lat = parts.find(x => x.hemisphere == 'N' || x.hemisphere == 'S');
        let lon = parts.find(x => x.hemisphere == 'E' || x.hemisphere == 'W');

        //CG: o singura emisfera data -> cealalta componenta e cealalta axa
        if (lat != null && lon == null) {
            lon = parts.find(x => x != lat);
        } else if (lon != null && lat == null) {
            lat = parts.find(x => x != lon);
        }

        if (lat != null && lon != null && lat != lon) {
            return Math.abs(lat.value) <= 90 && Math.abs(lon.value) <= 180 ? [this._wgs84Candidate(lon.value, lat.value)] : [];
        }

        return this._numericCandidates(parts[0].value, parts[1].value).filter(x => x.crsName == 'WGS84');
    }

    _wgs84Candidate(lon, lat) {
        return {
            lonLat: [lon, lat],
            name: this._format(Math.abs(lat), 6) + (lat < 0 ? ' S' : ' N') + ', ' + this._format(Math.abs(lon), 6) + (lon < 0 ? ' W' : ' E'),
            crsName: 'WGS84'
        };
    }

    //CG: intai cele din extinderea proiectiei curente, apoi cele mai apropiate de centrul vederii
    _rank(candidates) {
        let viewProjection = this.mapControl.getViewProjection();
        let projExtent = getProjection(viewProjection)?.getExtent();
        let center = this.mapControl.getViewCenter();

        let ranked = candidates.map(x => {
            let coord = transform(x.lonLat, 'EPSG:4326', viewProjection);
            let inside = projExtent == null || containsCoordinate(projExtent, coord);
            let distance = center != null ? Math.hypot(coord[0] - center[0], coord[1] - center[1]) : 0;
            return { candidate: x, inside: inside, distance: Number.isFinite(distance) ? distance : Infinity };
        });

        ranked.sort((x, y) => (y.inside - x.inside) || (x.distance - y.distance));
        return ranked.map(x => x.candidate);
    }

    _format(value, decimals) {
        return String(Number(value.toFixed(decimals)));
    }
}
